import { Observable } from 'rxjs';
import { ExtractionPreview } from '../../model/extraction-preview/extraction-preview.model';
import { ExtractionPreviewQueryParams } from '../../model/extraction-preview/extraction-preview-query-params.model';
import { PaginatedResponse } from '../../model/common/pagination.model';
import {
  SequencingReprocess,
  ReportSequencingReprocessRequest
} from '../../model/catalog/catalog.model';

/**
 * Result of batch approval operation
 */
export interface ApprovalResult {
  readonly success: boolean;
  readonly message: string;
  readonly approvedCount: number;
  readonly failedApprovals: FailedApproval[];
}

/**
 * Details of a failed approval
 */
export interface FailedApproval {
  readonly extractionId: string;
  readonly reason: string;
}

export interface BoxSummary {
  readonly boxCode: string;
  readonly createdAt: string;
  readonly occupiedPositions: number;
  readonly totalCapacity: number;
  readonly formattedOccupancy: string;
}

export interface StorageInventoryExportRequest {
  readonly boxCodes: string[];
  readonly startDate?: string;
  readonly endDate?: string;
}

/**
 * Abstract repository for Extraction Preview
 * Defines the contract for data access operations
 * HU28-922486: Listado de muestras de extracción
 *
 * This follows the Repository Pattern from Clean Architecture
 * - Domain layer defines the interface (this file)
 * - Infrastructure layer implements it (HttpExtractionPreviewRepository)
 */
export abstract class ExtractionPreviewRepository {
  /**
   * Get paginated list of extractions for preview
   * Only returns extractions in IN_PROCESS status
   * @param params Query parameters for sorting and pagination
   * @returns Observable with paginated extractions
   */
  abstract findAll(
    params?: ExtractionPreviewQueryParams
  ): Observable<PaginatedResponse<ExtractionPreview>>;

  /**
   * Approve extractions in batch
   * Changes extraction and specimen status to EXTRACTION_COMPLETED
   * @param extractionIds Array of extraction UUIDs to approve
   * @returns Observable with approval result
   */
  abstract approve(extractionIds: string[]): Observable<ApprovalResult>;

  /**
   * Export all extractions to CSV
   * Streams CSV content from backend with current sorting
   * @param sortBy Field to sort by
   * @param sortDirection Sort direction (ASC/DESC)
   * @returns Observable with CSV blob
   */
  abstract exportToCSV(sortBy: string, sortDirection: string): Observable<Blob>;

  abstract downloadStorageInventory(): Observable<Blob>;

  abstract listBoxesByDateRange(
    startDate: string,
    endDate: string
  ): Observable<BoxSummary[]>;

  abstract downloadStorageInventoryWithSelection(
    request: StorageInventoryExportRequest
  ): Observable<Blob>;

  /**
   * Report a specimen for sequencing reprocess
   * @param specimenId The specimen UUID to report
   * @param request The reprocess report data
   * @returns Observable with the created reprocess report
   */
  abstract reportSequencingReprocess(
    specimenId: string,
    request: ReportSequencingReprocessRequest
  ): Observable<SequencingReprocess>;
}
